import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Checkbox, MenuItem, Select, Typography } from "@mui/material";
import { GripVertical } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CHART_TYPE_LABELS } from "../../common/constants/cardRegistry";

const SortableCard = ({
  card,
  registry,
  onToggleVisible,
  onChangeChartType,
}) => {
  const { t } = useTranslation("common");
  const reg = registry.find((r) => r.key === card.key);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (!reg) return null;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 1,
        px: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: isDragging ? "action.hover" : "transparent",
        "&:hover": { backgroundColor: "action.hover" },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: "grab",
          display: "flex",
          alignItems: "center",
          color: "text.secondary",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <GripVertical size={16} />
      </Box>
      <Checkbox
        checked={card.visible}
        onChange={(e) => onToggleVisible(card.key, e.target.checked)}
        size="small"
      />
      <Typography
        variant="bodySm"
        sx={{ flex: 1, opacity: card.visible ? 1 : 0.5 }}
      >
        {t(reg.label)}
      </Typography>
      {reg.allowedChartTypes.length > 1 && (
        <Select
          value={card.chart_type || reg.defaultChartType}
          onChange={(e) => onChangeChartType(card.key, e.target.value)}
          size="small"
          sx={{ minWidth: 130, fontSize: "13px" }}
          disabled={!card.visible}
        >
          {reg.allowedChartTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {t(CHART_TYPE_LABELS[type])}
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  );
};

const SortableCardList = ({ cards, registry, configKey, onUpdate }) => {
  const { t } = useTranslation("settings");
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = cards.findIndex((c) => c.key === active.id);
    const newIndex = cards.findIndex((c) => c.key === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder: remove from old position, insert at new position, reassign orders
    const reordered = [...cards];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    const updated = reordered.map((card, i) => ({ ...card, order: i + 1 }));
    onUpdate(configKey, updated);
  };

  const handleToggleVisible = (cardKey, visible) => {
    const updated = cards.map((card) =>
      card.key === cardKey ? { ...card, visible } : card,
    );
    onUpdate(configKey, updated);
  };

  const handleChangeChartType = (cardKey, chartType) => {
    const updated = cards.map((card) =>
      card.key === cardKey ? { ...card, chart_type: chartType } : card,
    );
    onUpdate(configKey, updated);
  };

  return (
    <Box>
      <Typography variant="bodySm" color="text.secondary" sx={{ mb: 2 }}>
        {t("projectSettings.portalDisplay.cardConfigDescription")}
      </Typography>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={cards.map((c) => c.key)}
          strategy={verticalListSortingStrategy}
        >
          {cards.map((card) => (
            <SortableCard
              key={card.key}
              card={card}
              registry={registry}
              onToggleVisible={handleToggleVisible}
              onChangeChartType={handleChangeChartType}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default SortableCardList;
